import React from 'react'
import { graphql } from 'react-apollo'
import { useHistory } from 'react-router-dom'

import { AcornsIcon } from '@acorns/icons'
import gql from 'graphql-tag'
import { pathOr } from 'ramda'
import { compose, defaultProps, withProps } from 'recompose'
import styled from 'styled-components'

import {
  AlertMessage,
  MessageStatus,
  NotificationMessage,
} from 'generated/types'
import { QUERY } from 'utils/authentication'
import {
  EMPTY_NAV_BAR_CONTAINER,
  SIMPLE_NAV_BAR_X_BUTTON,
} from 'utils/testing/testIDs'

import { theme } from '../../theme'
import UserDropdown from './UserDropdown'

type Props = {
  renderNav?: (props: any) => React.ReactNode
  logoLink?: string
  navTitle?: string
  className?: string
  path?: string
  search?: string
}

type AddedProps = {
  closedAt?: string
  email?: string
  uuid?: string
  firstName?: string
  lastName?: string
  messageCount: number
  refetchMessages: () => void
}

type EnhancedProps = AddedProps & Props

const SizedContainer = styled.div`
  height: 80px;

  @media screen and (max-width: ${theme('breakpoints.md')}px) {
    height: 70px;
  }
`

const Container = styled(SizedContainer)`
  width: 100%;
`

export const Nav = styled(SizedContainer)`
  align-items: center;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  position: fixed;
  width: 100%;
  z-index: 40;
  box-shadow: rgba(0, 0, 0, 0.04) 0px 1px 0px;
`

const StyledCloseXButtonIcon = styled(AcornsIcon)`
  width: 16px;
  height: 16px;
  margin-left: 30px;
  cursor: pointer;
`

const StyledAcornsIcon = styled(AcornsIcon)`
  width: 27px;
  height: 33px;
  margin-left: 30px;
`

export type AllMessagesData = {
  allMessages?: (AlertMessage | NotificationMessage)[]
  refetch: () => void
}

const messagesQuery = gql`
  query allMessages($statusFilters: [MessageStatusFilterInput!]) {
    allMessages(statusFilters: $statusFilters) {
      ... on AlertMessage {
        id
      }
      ... on NotificationMessage {
        id
      }
    }
  }
`

const enhance = compose<EnhancedProps, Props>(
  graphql(QUERY, {
    props: ({ data }) => ({
      email: pathOr(null, ['user', 'email'], data),
      uuid: pathOr(null, ['user', 'uuid'], data),
      closedAt: pathOr(null, ['user', 'closedAt'], data),
      firstName: pathOr(null, ['user', 'firstName'], data),
      lastName: pathOr(null, ['user', 'lastName'], data),
    }),
    options: {
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    },
  }),
  graphql(messagesQuery, {
    options: {
      fetchPolicy: 'no-cache',
      variables: {
        statusFilters: [
          {
            key: MessageStatus.Read,
            value: false,
          },
        ],
      },
    },
    props: ({ data }: { data?: AllMessagesData }) => {
      const messageCount = data?.allMessages?.length || 0
      return {
        messageCount,
        refetchMessages: data?.refetch,
      }
    },
  }),
  withProps<
    Pick<Props, 'logoLink'>,
    Pick<Props & AddedProps, 'logoLink' | 'closedAt'>
  >(({ closedAt, logoLink }) => ({
    logoLink: closedAt ? '/' : logoLink,
  })),
  defaultProps({
    logoLink: '/',
    renderNav: (props: EnhancedProps) => (
      <>
        <UserDropdown
          uuid={props.uuid}
          email={props.email}
          closedAt={props.closedAt}
          firstName={props.firstName}
          lastName={props.lastName}
          messageCount={props.messageCount}
          refetchMessages={props.refetchMessages}
        />
      </>
    ),
  }),
)

export const EmptyNav = () => (
  <Container data-testid={EMPTY_NAV_BAR_CONTAINER}>
    <Nav>
      <StyledAcornsIcon icon={AcornsIcon.Icon.AcornsLogo} />
    </Nav>
  </Container>
)

export const SimpleNavbar = enhance((props) => {
  const history = useHistory()
  return (
    <Container className={props.className}>
      <Nav>
        <StyledCloseXButtonIcon
          onClick={() =>
            history.push({
              pathname: props.path,
              search: props.search,
            })
          }
          icon={AcornsIcon.Icon.CloseXIcon}
          data-testid={SIMPLE_NAV_BAR_X_BUTTON}
        />
      </Nav>
    </Container>
  )
})
